/** Cosmetic debris uses its own random stream and never mutates combat state. */
import { scaledSin } from '../sim/math.js';
export const PARTICLE_POOL_SIZE = 50;
const GRAVITY = 25;
const GRAVITY_CLAMP = 200;
const GROUND_Y = 186;
const HILL_Y = 166;
const HILL_X0 = 111;
const HILL_X1 = 143;
const FRAME_FIRST = 53;
const FRAME_LAST = 56;
export interface Particle {
  active: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  frame: number;
}
export const CRASH_PARTICLE_COUNT = 25;
export const CRASH_VX_SPREAD = 100;
export const CRASH_VY_MIN = -270;
export const CRASH_VY_MAX = -50;
export class FxRandom {
  private s: number;
  constructor(seed: number) {
    this.s = (seed | 0) === 0 ? 0x9e3779b9 : seed | 0;
  }
  below(n: number): number {
    if (n <= 0) return 0;
    let x = this.s;
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    this.s = x | 0;
    return Math.abs(this.s % n);
  }
}
export class ParticlePool {
  private readonly pool: Particle[] = [];
  constructor() {
    for (let i = 0; i < PARTICLE_POOL_SIZE; i++) {
      this.pool.push({ active: false, x: 0, y: 0, vx: 0, vy: 0, frame: FRAME_FIRST });
    }
  }
  clear(): void {
    for (const p of this.pool) p.active = false;
  }
  spawnCrashBurst(tick: number, mapX: number, mapY: number, heading: number): void {
    const drift = scaledSin(heading) >> 1;
    this.spawn(
      CRASH_PARTICLE_COUNT,
      mapX,
      mapY,
      drift - CRASH_VX_SPREAD,
      drift + CRASH_VX_SPREAD,
      CRASH_VY_MIN,
      CRASH_VY_MAX,
      new FxRandom((tick * 0x9e3779b1) ^ (mapX << 8) ^ mapY),
    );
  }
  spawn(
    count: number,
    mapX: number,
    mapY: number,
    vxMin: number,
    vxMax: number,
    vyMin: number,
    vyMax: number,
    rng: FxRandom,
  ): void {
    for (let i = PARTICLE_POOL_SIZE - 1; i >= 0 && count > 0; i--) {
      const p = this.pool[i]!;
      if (p.active) continue;
      p.active = true;
      p.x = mapX << 5;
      p.y = mapY << 5;
      p.vx = rng.below(vxMax - vxMin) + vxMin;
      p.vy = rng.below(vyMax - vyMin) + vyMin;
      p.frame = FRAME_FIRST;
      count--;
    }
  }
  stepAndDraw(
    ctx: CanvasRenderingContext2D,
    particles: readonly (HTMLImageElement | undefined)[],
    camX: number,
    camY: number,
  ): void {
    for (const p of this.pool) {
      if (!p.active) continue;
      const preX = p.x >> 5;
      const preY = p.y >> 5;
      const img = particles[p.frame - FRAME_FIRST];
      if (img) {
        const sx = preX - camX - (img.width >> 1);
        const sy = preY - camY - img.height;
        if (sx > -4 && sy > -4 && sx < 132 && sy < 132)
          ctx.drawImage(img, preX - (img.width >> 1), preY - img.height);
      }
      p.x += p.vx;
      p.y += p.vy;
      p.vy = Math.min(p.vy + GRAVITY, GRAVITY_CLAMP);
      const bounced = preY > GROUND_Y || (preY > HILL_Y && preX > HILL_X0 && preX < HILL_X1);
      if (bounced && p.vy > 0) {
        p.vy >>= 2;
        if (p.vy < 5) {
          p.active = false;
        }
        p.vy = toByte(-p.vy);
      }
      p.frame = p.frame >= FRAME_LAST ? FRAME_FIRST : p.frame + 1;
    }
  }
}
function toByte(v: number): number {
  return ((v & 0xff) << 24) >> 24;
}
export function particleSprites(
  get: (name: string) => HTMLImageElement | undefined,
): (HTMLImageElement | undefined)[] {
  const out: (HTMLImageElement | undefined)[] = [];
  for (let f = FRAME_FIRST; f <= FRAME_LAST; f++) out.push(get(`fx_${String(f).padStart(2, '0')}`));
  return out;
}
